import {
    Box,
    Modal, ModalContent, ModalHeader, ModalOverlay,
    useDisclosure,
    useSteps,
} from '@chakra-ui/react';
import {
    FormProvider, useForm,
} from 'react-hook-form';
import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { LeaseType, PortfolioPropertyTag } from '@acp/shared';
import { Stepper } from '@acp/core-ui';
import { TenancyStep, tenancySteps } from '../tenancy-steps';
import { ConfirmationScreen, LeaseAgreementScreen, OccupantsScreen } from '../screens';
import { formSchema, FormType, FormValues } from '../schemas';
import { ConfirmCloseDialog } from '../dialogs';
import { useProperty } from '../../../property/hooks/use-property';
import { IntegratedPropertyDialog } from '../../../property/components/integrated-property-dialog';

export function AddTenancyModal({
    propertyId,
    isOpen,
    onClose,
}: {
    propertyId: number;
    isOpen: boolean;
    onClose: () => void;
}) {
    const propertyQuery = useProperty(propertyId);
    const tags = propertyQuery.data?.tags ?? [];
    const isPropertyIntegrated = tags.includes(PortfolioPropertyTag.Integrated);

    const { activeStep, setActiveStep } = useSteps({
        index: TenancyStep.LeaseAgreement,
        count: tenancySteps.length,
    });

    const confirmCloseDialogDisclosure = useDisclosure();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            propertyId,
            tenancy: {
                formType: FormType.Add,
                tenancyId: null,
                leaseType: LeaseType.New,
                startDate: new Date(),
                endDate: null,
                vacateDate: null,
                notes: '',
            },
            occupants: [],
        },
        disabled: isPropertyIntegrated,
        mode: 'onChange',
    });

    const {
        formState: { isDirty },
        reset,
    } = form;

    const handleClose = () => {
        confirmCloseDialogDisclosure.onClose();
        setActiveStep(TenancyStep.LeaseAgreement);
        reset();
        onClose();
    };

    const handleConfirmCloseDialog = () => {
        // If the form has been modified, confirm with the user if they
        // want to save their changes before closing the modal
        if (isDirty) {
            confirmCloseDialogDisclosure.onOpen();
        } else {
            handleClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            reset();
            setActiveStep(TenancyStep.LeaseAgreement);
        }
    }, [isOpen]);

    return (
        <Modal
            // Prevent this modal from closing when clicking on the overlay
            closeOnOverlayClick={false}
            isOpen={isOpen}
            autoFocus={false}
            onClose={onClose}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <FormProvider {...form}>
                    <ModalHeader>Add tenancy</ModalHeader>
                    {
                        isPropertyIntegrated && (
                            <Box px={6} pb={4}>
                                <IntegratedPropertyDialog
                                    title="Tenancy data for this property is integrated with your property management software"
                                />
                            </Box>
                        )
                    }
                    <Box mt={4} px={6} pb={6}>
                        <Stepper activeStep={activeStep} steps={tenancySteps} />
                    </Box>
                    {
                        activeStep === TenancyStep.LeaseAgreement && (
                            <LeaseAgreementScreen
                                onSetScreen={setActiveStep}
                                handleClose={handleConfirmCloseDialog}
                            />
                        )
                    }
                    {
                        activeStep === TenancyStep.Occupants && (
                            <OccupantsScreen onSetScreen={setActiveStep} />
                        )
                    }
                    {
                        activeStep === TenancyStep.Confirmation && (
                            <ConfirmationScreen
                                onSetScreen={setActiveStep}
                                onClose={onClose}
                            />
                        )
                    }
                </FormProvider>
            </ModalContent>
            <ConfirmCloseDialog
                alertDialogDisclosure={confirmCloseDialogDisclosure}
                handleClose={handleClose}
            />
        </Modal>
    );
}
